<template>
  <LineChartGenerator
    :options="options"
    :data="chartData"
    :plugins="plugins"
    :css-classes="cssClasses"
    :styles="styles"
    :width="width"
    :height="height"
  />
</template>

<script setup lang="ts">
import { Line as LineChartGenerator } from 'vue-chartjs'
import resolveConfig from 'tailwindcss/resolveConfig'
import { reformat } from '~/utils/date'
import { numberFormatter } from '@/utils/number'
import tailwindConfig from '~/tailwind.config'
import { logError } from '~/utils/log-error'
import type { TCommon, TRecord } from '~/src/types/common'
import type { ChartData, Point } from 'chart.js'

const fullConfig = resolveConfig(tailwindConfig)

withDefaults(
  defineProps<{
    chartData: ChartData<'line', (number | Point)[], unknown>
    width?: number
    height?: number
    cssClasses?: string
    styles?: TRecord
    plugins?: TCommon[]
  }>(),
  {
    width: 400,
    height: 400,
    cssClasses: '',
    styles: () => ({}),
    plugins: () => [],
  },
)

const options = ref<TCommon>({
  responsive: true,
  maintainAspectRatio: false,
  elements: {
    line: {
      tension: 0, // disables bezier curves
    },
    point: {
      radius: 0,
    },
  },
  scales: {
    y: {
      display: true,
      position: 'left',
      ticks: {
        font: {
          family: 'Montserrat',
          size: 12,
          color: fullConfig.theme.colors.s['900'],
        },
        padding: 10,
        beginAtZero: false,
        callback(value) {
          return value > 0 ? `${numberFormatter(value)}` : value
        },
      },
    },
    x: {
      type: 'time',
      time: {
        unit: 'month',
      },
      bounds: 'ticks',
      stacked: false,
      ticks: {
        font: {
          family: 'Montserrat',
          color: fullConfig.theme.colors.s['900'],
        },
        autoSkip: true,
        autoSkipPadding: 30,
      },
    },
  },
  onHover: (event) => {
    EventBus.$emit('index-chart-hover', event)
  },
  plugins: {
    crosshair: {
      line: {
        color: fullConfig.theme.colors.s['500'],
        width: 1,
      },
      zoom: {
        enabled: false,
      },
      snap: {
        enabled: true,
      },
      sync: {
        enabled: false,
      },
    },
    htmlLegend: {
      containerID: 'legend-container',
    },
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    tooltip: {
      enabled: true,
      mode: 'nearest',
      axis: 'x',
      intersect: false,
      titleFont: {
        family: 'Montserrat',
        size: 14,
      },
      bodyFont: {
        family: 'Montserrat',
        size: 14,
      },
      callbacks: {
        label(context) {
          const { dataIndex, dataset } = context
          const { label } = context.dataset
          const labelReplaceFirst = label.replace(/[*]+/g, '').trim()
          return `${labelReplaceFirst}: ${dataset.data[dataIndex].y}`
        },
        labelColor(context) {
          return {
            backgroundColor: context.dataset.borderColor,
          }
        },
        title(tooltipItems) {
          const { dataIndex } = tooltipItems[0]
          const tooltipItem = tooltipItems[0].dataset.data[dataIndex]
          const date = tooltipItem.x

          try {
            return reformat(date, 'MMM dd, yyyy')
          } catch (e) {
            logError(e)
          }
        },
      },
    },
  },
})
</script>
